import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  EvidenceRepository,
  ExpectedRevision,
  Page,
  PageRequest,
  PersistedEvidence,
  RepositoryResult,
} from "../contracts";
import type { Database } from "../database.generated";
import type { EvidenceRecordRow } from "../database.types";
import { mapEvidenceRow } from "../mappers";
import { isSha256 } from "./checksum";
import { evidencePayloadCodec } from "./codecs";
import { revisionConflict } from "./errors";
import { createIdPage, cursorId, databaseFailure, notFound, validationFailure } from "./repository-helpers";

export class SupabaseEvidenceRepository implements EvidenceRepository {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getById(id: string): Promise<RepositoryResult<PersistedEvidence>> {
    const { data, error } = await this.client.from("evidence_records").select("*").eq("id", id).maybeSingle();
    if (error) return databaseFailure(error, "The evidence record could not be read.");
    if (!data) return notFound("Evidence record");
    return mapEvidenceRow(data as EvidenceRecordRow);
  }

  listForOrganisation(organisationId: string, page: PageRequest) {
    return this.list({ organisationId }, page);
  }

  listForAudit(auditId: string, page: PageRequest) {
    return this.list({ auditId }, page);
  }

  listForSection(sectionId: string, page: PageRequest) {
    return this.list({ sectionId }, page);
  }

  private async list(
    filter: { organisationId?: string; auditId?: string; sectionId?: string },
    page: PageRequest,
  ): Promise<RepositoryResult<Page<PersistedEvidence>>> {
    const scope = filter.organisationId
      ? `organisation:${filter.organisationId}`
      : filter.auditId
        ? `audit:${filter.auditId}`
        : `section:${filter.sectionId ?? ""}`;
    const order = `id.asc|${scope}`;
    const after = cursorId(page, order);
    if (!after.ok) return after;
    let query = this.client.from("evidence_records").select("*").order("id", { ascending: true }).limit(page.limit + 1);
    if (filter.organisationId) query = query.eq("organisation_id", filter.organisationId);
    if (filter.auditId) query = query.eq("audit_engagement_id", filter.auditId);
    if (filter.sectionId) query = query.eq("report_section_id", filter.sectionId);
    if (after.value) query = query.gt("id", after.value);
    const { data, error } = await query;
    if (error) return databaseFailure(error, "Evidence records could not be listed.");
    return createIdPage((data ?? []) as EvidenceRecordRow[], page, mapEvidenceRow, order);
  }

  async create(
    record: Omit<PersistedEvidence, "revision" | "createdAt" | "updatedAt">,
  ): Promise<RepositoryResult<PersistedEvidence>> {
    const payload = evidencePayloadCodec.decode(record.payload, record.payload.schemaVersion);
    if (!payload.ok) return validationFailure(payload.error.message);
    if (!isSha256(record.payloadChecksum)) return validationFailure("Evidence payload checksum must be SHA-256.");
    const { data, error } = await this.client.from("evidence_records").insert({
      id: record.id,
      organisation_id: record.organisationId,
      audit_engagement_id: record.auditEngagementId ?? null,
      report_section_id: record.reportSectionId ?? null,
      evidence_type: record.evidenceType,
      source_type: record.sourceType,
      source_id: record.sourceId ?? null,
      observed_at: record.observedAt ?? null,
      payload_schema_version: payload.value.schemaVersion,
      payload: payload.value,
      payload_checksum: record.payloadChecksum,
      visibility: record.visibility,
      status: record.status,
      archived_at: record.archivedAt ?? null,
    }).select("*").single();
    if (error) return databaseFailure(error, "The evidence record could not be created.");
    return mapEvidenceRow(data as EvidenceRecordRow);
  }

  async update(
    id: string,
    changes: Partial<
      Pick<PersistedEvidence, "payload" | "payloadChecksum" | "observedAt" | "visibility" | "status" | "archivedAt">
    >,
    options: ExpectedRevision,
  ): Promise<RepositoryResult<PersistedEvidence>> {
    const row: Database["public"]["Tables"]["evidence_records"]["Update"] = {};
    if (changes.payload !== undefined) {
      const payload = evidencePayloadCodec.decode(changes.payload, changes.payload.schemaVersion);
      if (!payload.ok) return validationFailure(payload.error.message);
      row.payload_schema_version = payload.value.schemaVersion;
      row.payload = payload.value;
    }
    if (changes.payloadChecksum !== undefined) {
      if (!isSha256(changes.payloadChecksum)) return validationFailure("Evidence payload checksum must be SHA-256.");
      row.payload_checksum = changes.payloadChecksum;
    }
    if ("observedAt" in changes) row.observed_at = changes.observedAt ?? null;
    if (changes.visibility !== undefined) row.visibility = changes.visibility;
    if (changes.status !== undefined) row.status = changes.status;
    if ("archivedAt" in changes) row.archived_at = changes.archivedAt ?? null;
    if (!Object.keys(row).length) return validationFailure("At least one evidence change is required.");
    const { data, error } = await this.client.from("evidence_records").update(row)
      .eq("id", id).eq("revision", options.expectedRevision).select("*").maybeSingle();
    if (error) return databaseFailure(error, "The evidence record could not be updated.");
    if (data) return mapEvidenceRow(data as EvidenceRecordRow);
    const current = await this.getById(id);
    return current.ok ? { ok: false, error: revisionConflict() } : current;
  }

  archive(id: string, archivedAt: string, options: ExpectedRevision) {
    return this.update(id, { status: "Archived", archivedAt }, options);
  }

  quarantine(id: string, options: ExpectedRevision) {
    return this.update(id, { status: "Quarantined" }, options);
  }
}
